import { MenuItem, Theme } from '@mui/material'
import { styled } from '@mui/material/styles'
import { MUIThemeColors } from 'src/mui-theme'
import { GetRGBA, getThemeColor } from 'src/utils'

export const PositionDrawingFilterMenuItem = styled(MenuItem, {
  shouldForwardProp: prop => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  fontSize: '13px',
  fontWeight: isSelected ? 600 : 400,
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
}))

export const getPositionDrawingFilterSurfaceSx = (theme: Theme, padding: number) => ({
  p: padding ?? 0,
  mb: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  borderColor: theme.palette.divider,
})

export const getPositionDrawingSearchBarSx = (theme: Theme, compact = false) => ({
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: compact ? ('column' as const) : ('row' as const),
  height: compact ? 'auto' : 40,
  bgcolor: GetRGBA(getThemeColor(theme, MUIThemeColors.paleBlue), 0.35),
  overflow: 'hidden',
})

export const getPositionDrawingCompactSearchInputSx = (theme: Theme) => ({
  ...getPositionDrawingSearchInputSx(theme),
  width: '100%',
  py: 0.75,
})

export const getPositionDrawingSearchInputSx = (theme: Theme) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  px: 1.25,
  gap: 0.75,
  minWidth: 0,
  '& input': {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '14px',
    color: theme.palette.text.primary,
    fontFamily: 'inherit',
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
})

export const getPositionDrawingFilterButtonSx = (theme: Theme) => ({
  textTransform: 'uppercase' as const,
  fontWeight: 700,
  fontSize: '14px',
  letterSpacing: '0.6px',
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
  borderRadius: 0,
  minWidth: 96,
  px: 1.5,
  flexShrink: 0,
  '&:hover': {
    bgcolor: GetRGBA(getThemeColor(theme, MUIThemeColors.paleBlue), 0.5),
  },
})

export const getPositionDrawingActiveFiltersRowSx = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 0.75,
  minHeight: 28,
})

export const getPositionDrawingChipSx = (theme: Theme) => ({
  height: 28,
  borderRadius: '14px',
  bgcolor: getThemeColor(theme, MUIThemeColors.primaryDark),
  color: getThemeColor(theme, MUIThemeColors.white),
  fontSize: '14px',
  fontWeight: 500,
  '& .MuiChip-label': { px: 0.75 },
  '& .MuiChip-icon': {
    color: getThemeColor(theme, MUIThemeColors.white),
    ml: 0.75,
    mr: -0.25,
    opacity: 0.85,
  },
  '& .MuiChip-deleteIcon': {
    color: getThemeColor(theme, MUIThemeColors.white),
    fontSize: 16,
    opacity: 0.9,
    '&:hover': { color: getThemeColor(theme, MUIThemeColors.white), opacity: 1 },
  },
})

export const getPositionDrawingClearAllSx = (theme: Theme) => ({
  fontWeight: 700,
  fontSize: '14px',
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  flexShrink: 0,
  whiteSpace: 'nowrap' as const,
  '&:hover': {
    textDecoration: 'underline',
    color: getThemeColor(theme, MUIThemeColors.primaryDark),
  },
})

export const getPositionDrawingFilterDividerSx = (theme: Theme) => ({
  alignSelf: 'stretch',
  borderColor: theme.palette.divider,
})
